import React from "react";
import { Box, Typography } from "@mui/material";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { getFontSize, FONT_SIZES } from '../utils/fontSizeUtils';

interface ThinkingIndicatorProps {
  thinkingSteps: string[];
  currentStep: number;
  isEmbedded?: boolean;
}

export function getAnimatedStepLabel(text: string): string | null {
  const normalized = text.trim().toLowerCase().replace(/\s+/g, " ");
  const label = normalized.replace(/(?:\s*\.\s*){1,3}$/, "").trim();
  if (label === "connecting") return "connecting";
  if (label.includes("secure shell")) return label;
  if (label.startsWith("running ") && label.includes(" script")) return label;
  return null;
}

function ConnectingAnimatedStep({ label, fontSizePx }: { label: string; fontSizePx: number }) {
  const color = "#6b7280";
  /* Slight upward offset so dots optically align with the word (replaces older flex-end + positive Y shift). */
  const dotLiftPx = -Math.max(1, Math.round(fontSizePx * 0.1));
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        ml: 2,
      }}
    >
      <Typography
        component="span"
        sx={{
          color,
          fontSize: `${fontSizePx}px`,
          lineHeight: 1.2,
          fontFamily: "Inter, sans-serif",
          fontWeight: 400,
        }}
      >
        {label}
      </Typography>
      <Box
        aria-hidden
        sx={{
          display: "inline-flex",
          gap: "4px",
          alignItems: "center",
          animation: "connectingShuttle 0.85s ease-in-out infinite alternate",
          "@keyframes connectingShuttle": {
            from: { transform: `translate(-4px, ${dotLiftPx}px)` },
            to: { transform: `translate(5px, ${dotLiftPx}px)` },
          },
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            component="span"
            data-testid="animated-step-dot"
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: color,
              animation: "connectingDotPulse 1s ease-in-out infinite",
              animationDelay: `${i * 0.18}s`,
              "@keyframes connectingDotPulse": {
                "0%, 100%": { opacity: 0.25, transform: "scale(0.8)" },
                "50%": { opacity: 1, transform: "scale(1)" },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({
  thinkingSteps,
  currentStep,
  isEmbedded = false,
}) => {
  const workingFontSize = getFontSize(FONT_SIZES.BODY, isEmbedded);
  const stepFontSize = getFontSize(FONT_SIZES.MEDIUM, isEmbedded);
  
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        p: 2,
        background: "#fff",
        borderRadius: 2,
        border: "0px solid #e5e7eb",
        maxWidth: "400px",
      }}
    >
      {/* Main Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: isEmbedded ? 20 : 24,
            height: isEmbedded ? 20 : 24,
            borderRadius: "50%",
            border: "2px solid #3b82f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
          }}
        >
          <LightbulbIcon
            sx={{
              fontSize: isEmbedded ? 12 : 14,
              color: "#3b82f6",
            }}
          />
        </Box>
        <Typography
          sx={{
            color: "#6b7280",
            fontSize: `${workingFontSize}px`,
            fontWeight: 500,
            fontFamily: "Inter, sans-serif",
          }}
        >
          Working...
        </Typography>
      </Box>

      {/* Timeline Steps */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          pl: 2,
        }}
      >
        {/* Vertical Line */}
        <Box
          sx={{
            position: "absolute",
            left: 5,
            top: 0,
            bottom: 0,
            width: "1px",
            background: "#e5e7eb",
          }}
        />

        {thinkingSteps.map((step, index) => {
          const animatedLabel = getAnimatedStepLabel(step);
          const shouldAnimate = Boolean(animatedLabel && index === thinkingSteps.length - 1);
          const displayStep = animatedLabel || step;
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: index < thinkingSteps.length - 1 ? 1.5 : 0,
                position: "relative",
              }}
            >
              {/* Step Dot */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: index <= currentStep ? "#6b7280" : "#d1d5db",
                  position: "absolute",
                  left: -6,
                  zIndex: 1,
                }}
              />

              {/* Step Text */}
              {shouldAnimate ? (
                <ConnectingAnimatedStep label={animatedLabel as string} fontSizePx={stepFontSize} />
              ) : (
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: `${stepFontSize}px`,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    ml: 2,
                  }}
                >
                  {displayStep}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ThinkingIndicator;
